// FP 간트 — 시간축 스케일 / 헤더 눈금 계산
//
// 그리기는 화면별 컴포넌트(셀 렌더러 / 캔버스)가 맡고, 여기서는 좌표와 눈금만 만든다.
// **d3를 쓰지 않는다** — 시간축이 선형이라 `(ts - t0) * pxPerMs` 한 줄이면 되고, 유일하게 손이
// 가는 "일/주/월 경계 생성"은 이 저장소의 날짜 관례인 `date-fns`가 그대로 커버한다
// (근거: reports/gantt-implementation.md §3).

import {
  eachDayOfInterval,
  eachHourOfInterval,
  eachMonthOfInterval,
  eachWeekOfInterval,
  eachYearOfInterval,
  startOfDay,
  startOfHour,
  startOfMonth,
  startOfWeek,
  startOfYear
} from "date-fns";

import { HEADER_UNITS, DEFAULT_HEADER_UNIT, WEEKEND_DAYS } from "@plannel/pages/factory-plan/common/gantt/ganttConstants";

const MS_PER_HOUR = 60 * 60 * 1000;

// Date | ISO 문자열 | epoch ms 를 모두 받는다. 파싱 실패 시 NaN(호출부에서 걸러짐).
export const toMs = (value) => {
  if (value == null) return NaN;
  if (value instanceof Date) return value.getTime();
  if (typeof value === "number") return value;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? NaN : parsed;
};

const unitMeta = (unit) => HEADER_UNITS.find(u => u.value === unit) ?? HEADER_UNITS.find(u => u.value === DEFAULT_HEADER_UNIT);

// 시간축 스케일. startTs~endTs 구간을 unit/zoom에 따른 픽셀 폭으로 선형 매핑한다.
//  - x(ts)      : 시각 → 컬럼 내부 x(px)
//  - invert(px) : x(px) → 시각(ms)  ※ 히트테스트/툴팁 위치 계산용
//  - width      : 타임라인 컬럼 전체 폭(px) = AG Grid 컬럼 폭으로 쓴다
export const buildTimeScale = ({ startTs, endTs, unit = DEFAULT_HEADER_UNIT, zoom = 1 }) => {
  const meta = unitMeta(unit);
  const t0 = toMs(startTs);
  const t1 = toMs(endTs);

  if (Number.isNaN(t0) || Number.isNaN(t1) || t1 <= t0) return null;

  const pxPerMs = (meta.pxPerUnit * zoom) / (meta.unitHours * MS_PER_HOUR);

  return {
    unit: meta.value,
    zoom,
    t0,
    t1,
    pxPerMs,
    width: Math.max(1, Math.round((t1 - t0) * pxPerMs)),
    x: (ts) => (toMs(ts) - t0) * pxPerMs,
    invert: (px) => t0 + px / pxPerMs
  };
};

// old OnGetGanttHeader의 주 버킷 라벨 — "20(월) ~ 26(일)" 형태.
const dayWithWeekday = (date, weekdayLabels) => {
  const label = weekdayLabels?.[date.getDay()];
  return label ? `${date.getDate()}(${label})` : String(date.getDate());
};

const pad2 = (n) => String(n).padStart(2, "0");

// 단위별 경계 생성기. 시작 경계를 구간 시작보다 앞으로 내려서(startOf*) 첫 칸이 잘려도 라벨이
// 정상 배치되게 한다 — 왼쪽으로 삐져나간 부분은 아래 toTicks에서 클램프된다.
const BOTTOM_BUILDERS = {
  Hour: (start, end) => eachHourOfInterval({ start: startOfHour(start), end }),
  Day: (start, end) => eachDayOfInterval({ start: startOfDay(start), end }),
  Week: (start, end, weekStartsOn) => eachWeekOfInterval({ start: startOfWeek(start, { weekStartsOn }), end }, { weekStartsOn }),
  Month: (start, end) => eachMonthOfInterval({ start: startOfMonth(start), end })
};

// 하위(sub) 격자 — 라벨 없이 선만 긋는 보조 경계. 날짜 경계(bottom)보다 더 옅게 그려서 "하루 안에서
// 몇 시쯤"을 읽게 해준다(§7-2a ①의 3단 위계 중 막내). bottom이 이미 그리는 경계는 제외한다.
const SUB_BUILDERS = {
  Hour: () => [],                                       // bottom이 시간 단위라 하위가 없다
  Day: (start, end) => eachHourOfInterval({ start: startOfDay(start), end }).filter(d => d.getHours() % 6 === 0 && d.getHours() !== 0),
  Week: (start, end) => eachDayOfInterval({ start: startOfDay(start), end }).filter(d => d.getDay() !== 0),
  Month: (start, end) => eachWeekOfInterval({ start: startOfWeek(start), end }).filter(d => d.getDate() > 7)
};

// 상단(병합) 라벨 축 — 하단이 시간이면 일, 일/주면 월, 월이면 연.
const TOP_BUILDERS = {
  Hour: (start, end) => eachDayOfInterval({ start: startOfDay(start), end }),
  Day: (start, end) => eachMonthOfInterval({ start: startOfMonth(start), end }),
  Week: (start, end) => eachMonthOfInterval({ start: startOfMonth(start), end }),
  Month: (start, end) => eachYearOfInterval({ start: startOfYear(start), end })
};

const BOTTOM_LABELS = {
  Hour: (date) => pad2(date.getHours()),
  Day: (date, { weekdayLabels }) => dayWithWeekday(date, weekdayLabels),
  Week: (date, { weekdayLabels, nextDate }) => {
    const last = new Date((nextDate?.getTime() ?? date.getTime() + 7 * 24 * MS_PER_HOUR) - 1);
    return `${dayWithWeekday(date, weekdayLabels)} ~ ${dayWithWeekday(last, weekdayLabels)}`;
  },
  Month: (date) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}`
};

const TOP_LABELS = {
  Hour: (date, { weekdayLabels }) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}(${weekdayLabels?.[date.getDay()] ?? ""})`,
  Day: (date) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}`,
  Week: (date) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}`,
  Month: (date) => String(date.getFullYear())
};

// 날짜 판독의 **앵커**가 되는 경계 판정 — 격자를 3단 위계로 그리기 위한 표시다(§7-2a ①).
// 단위별로 "한 단 위 축이 바뀌는 자리"를 major로 본다: 시 → 자정, 일 → **1일**, 주 → 월 변경,
// 월 → 연 변경. 이 자리만 진한 선을 그으면 일 경계선을 옅게 둬도 며칠째인지 셀 수 있다.
// **이 플래그는 캔버스 격자(`drawTimelineGrid`)와 헤더 눈금 칸(`GanttFrame`의 `borderLeft`) 두 곳을
// 동시에 움직인다** — 판정을 바꾸면 두 곳이 같이 바뀐다(그게 의도다. 한쪽만 바꾸면 어긋난다).
const isMajorBoundary = (date, unit) => {
  switch (unit) {
    case "Hour": return date.getHours() === 0;
    // ⚠️ **월요일은 major가 아니다**(2026-08-21 정정). 원래는 `getDay() === 1`도 포함했는데, 그러면
    // 주말 밴드(토·일 음영)의 **오른쪽 끝(= 월요일 시작)만 진한 선**이 되어 밴드가 좌우 비대칭으로
    // 보였다(사용자 지적: "토요일 시작 테두리와 일요일 끝 테두리 굵기가 다르다"). 캔버스에서는
    // `WEEKLINE`(검정 22%) vs `GRIDLINE`(11%)으로 2배, 헤더에서는 major 칸의 `borderLeft`가 이전 칸의
    // `borderRight`에 겹쳐 그 경계만 2px이 됐다 — 같은 `major` 플래그가 두 곳을 동시에 움직인다.
    //
    // 주 경계 앵커를 없애도 판독은 유지된다: **주말 밴드 자체가 주 리듬을 말해준다**(§7-6에서 밴드가
    // 추가되면서 §7-2a ①의 주 앵커선이 중복이 됐다). 월 경계(1일)는 밴드가 대신할 수 없으므로 남긴다.
    case "Day": return date.getDate() === 1;
    case "Week": return date.getDate() <= 7;
    case "Month": return date.getMonth() === 0;
    default: return false;
  }
};

// 경계 배열 → 렌더용 눈금 배열. 컬럼 폭을 벗어나는 부분은 잘라내고, 폭이 0 이하로 남는 칸은 버린다.
const toTicks = (dates, scale, labelFn, options) => {
  const ticks = [];

  dates.forEach((date, index) => {
    const nextDate = dates[index + 1] ?? new Date(scale.t1);
    // **정수로 스냅한다.** `scale.x()`는 소수를 돌려주는데(예: 719.1), 헤더 눈금 칸은 그 값을
    // `left`/`width`로 절대배치하는 DOM이고 `box-sizing: border-box`라 1px 테두리가 칸 **안쪽**
    // 마지막 픽셀에 그려진다. 소수 경계에서는 다음 칸의 박스가 그 픽셀을 덮어(칸은 DOM 순서상
    // 나중에 그려진다) **앞 칸의 오른쪽 테두리가 사라진다** — 주말 밴드처럼 배경색이 있는 칸에서
    // 특히 눈에 띄었다("토요일 시작 부분에 border가 안 그려진다", 2026-08-21 사용자 지적).
    // 인접 칸이 `scale.x(nextDate)`라는 **같은 값**을 공유하므로 각자 반올림해도 결과가 일치한다
    // → 겹침도 틈도 없이 정확히 맞물린다. 캔버스 격자도 `px(left) - 1`(= 같은 정수의 이전 픽셀)에
    // 그리므로 헤더 테두리와 위치가 어긋나지 않는다.
    const left = Math.max(0, Math.round(scale.x(date)));
    const right = Math.round(Math.min(scale.x(nextDate), scale.width));
    const width = right - left;

    if (width <= 0) return;

    ticks.push({
      key: `${date.getTime()}`,
      left,
      width,
      label: labelFn(date, { ...options, nextDate }),
      // 일 단위에서 토/일 강조 — **헤더 칸 전용 플래그다.** 본문 밴드는 버킷과 무관하게
      // `buildWeekendRanges`(날짜 기준)가 만든다(§7-6). 헤더는 칸이 곧 하루인 일 버킷에서만
      // 칠할 수 있다(주·월 버킷은 칸이 7일·한 달이라 성립하지 않는다).
      weekend: scale.unit === "Day" && (date.getDay() === 0 || date.getDay() === 6),
      major: isMajorBoundary(date, scale.unit)
    });
  });

  return ticks;
};

// 2단 헤더 눈금(상단 병합 + 하단 단위)과 본문 세로 격자선용 좌표를 한 번에 만든다.
export const buildTicks = (scale, { weekdayLabels, weekStartsOn = 0 } = {}) => {
  if (!scale) return { top: [], bottom: [], sub: [] };

  const start = new Date(scale.t0);
  const end = new Date(scale.t1);
  const unit = scale.unit;

  const bottomDates = (BOTTOM_BUILDERS[unit] ?? BOTTOM_BUILDERS.Day)(start, end, weekStartsOn);
  const topDates = (TOP_BUILDERS[unit] ?? TOP_BUILDERS.Day)(start, end);
  const subDates = (SUB_BUILDERS[unit] ?? SUB_BUILDERS.Day)(start, end);

  return {
    top: toTicks(topDates, scale, TOP_LABELS[unit] ?? TOP_LABELS.Day, { weekdayLabels }),
    bottom: toTicks(bottomDates, scale, BOTTOM_LABELS[unit] ?? BOTTOM_LABELS.Day, { weekdayLabels }),
    sub: toTicks(subDates, scale, () => "", {})
  };
};

/**
 * **보이는 눈금 칸의 인덱스 범위** — 가로축 가상화. `ganttRowModel.visibleRowRange` 의 거울상이다.
 *
 * ⚠️ **왜 필요한가.** 헤더 눈금은 축 전체를 DOM 으로 렌더한다. Hour 버킷 + 1년 계획이면
 * **하단 8,760 + 상단 366 = 9,126개**의 MUI `sx` Box 다(일자 버킷은 379개). 버킷을 '시간'으로
 * 바꾸는 순간 그게 한꺼번에 생성되면서 메인 스레드가 막혀 **화면이 멈춘 것처럼 보였다**
 * (2026-09-10 신고. 시간 → 일/주/월 방향은 빠르다 — 개수가 줄어드는 쪽이라서다).
 * 데이터 생성(`buildTicks`)은 13ms 로 무죄였고, 비용은 **DOM 생성 + `sx` 직렬화**였다.
 *
 * ⚠️ **첫 칸은 `left` 로 판정하면 안 된다.** 상단(일·월) 칸은 넓어서 **화면을 덮고 있는 칸의 `left`가
 * 뷰포트 왼쪽보다 앞**일 수 있다 — 그걸 잘라내면 그 칸이 사라진다. 오른쪽 끝(`left + width`)을 봐야
 * 한다. 캔버스 격자(`ganttDraw.drawTimelineGrid`)가 이미 같은 방식으로 컬링한다.
 *
 * 눈금은 맞닿아 있어(앞 칸의 오른쪽 끝 = 다음 칸의 왼쪽) `left` 와 `left + width` 가 **둘 다 오름차순**
 * 이므로 이진탐색이 성립한다.
 *
 * @param buffer 뷰포트 밖으로 더 렌더할 폭(px). 0이면 1px 스크롤마다 범위가 바뀌어 재렌더가 폭주한다
 *               (세로축이 `buffer = 2`행을 두는 것과 같은 이유).
 * @returns `{ first, last }` — `last < first` 면 그릴 칸이 없다
 */
export const visibleTickRange = (ticks = [], scrollLeft = 0, viewportWidth = 0, buffer = 0) => {
  const count = ticks.length;
  if (count === 0) return { first: 0, last: -1 };

  const from = scrollLeft - buffer;
  const to = scrollLeft + viewportWidth + buffer;

  const lastTick = ticks[count - 1];
  if (lastTick.left + lastTick.width <= from) return { first: 0, last: -1 };
  if (ticks[0].left >= to) return { first: 0, last: -1 };

  // 첫 칸 — 오른쪽 끝이 `from` 을 넘어서는 첫 인덱스.
  let lo = 0;
  let hi = count - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (ticks[mid].left + ticks[mid].width > from) hi = mid;
    else lo = mid + 1;
  }
  const first = lo;

  // 마지막 칸 — 왼쪽 끝이 `to` 보다 앞인 마지막 인덱스.
  lo = first;
  hi = count - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (ticks[mid].left < to) lo = mid;
    else hi = mid - 1;
  }

  return { first, last: lo };
};

// 주말 밴드 — **버킷과 무관하게** 날짜 기준으로 구간을 만든다.
//
// 예전에는 헤더 눈금 칸에 `weekend` 플래그를 켜고 그 칸을 그대로 칠했다. 그래서 일 버킷에서만 동작했다 —
// 주 버킷은 칸 1개가 7일, 월은 한 달이라 "이 칸은 주말"이 성립하지 않고 칸의 일부만 칠할 방법도 없었다.
// 여기서 날짜 구간 배열을 만들어 `ganttDraw.drawWeekends`가 `scale.x()`로 위치를 잡으면 버킷과 무관해진다
// (휴무일 밴드가 이미 쓰는 방식과 같다 — reports/gantt-implementation.md §7-6).
//
// **토·일이 붙어 있으면 한 구간으로 합친다** — 반투명 면이라 두 번 칠하면 경계에서 색이 겹쳐 진해진다.
export const buildWeekendRanges = (scale, { weekendDays = WEEKEND_DAYS } = {}) => {
  if (!scale) return [];

  const days = eachDayOfInterval({ start: startOfDay(new Date(scale.t0)), end: new Date(scale.t1) });
  const ranges = [];

  days.forEach(day => {
    if (!weekendDays.includes(day.getDay())) return;

    const startTs = Math.max(day.getTime(), scale.t0);
    const endTs = Math.min(day.getTime() + 24 * MS_PER_HOUR, scale.t1);
    if (endTs <= startTs) return;

    const last = ranges[ranges.length - 1];
    if (last && last.endTs === startTs) last.endTs = endTs;   // 토→일 연속이면 이어 붙인다
    else ranges.push({ startTs, endTs });
  });

  return ranges;
};

const isSameDay = (a, b) => startOfDay(new Date(a)).getTime() === startOfDay(new Date(b)).getTime();

// old `common/utils/gantt.js`의 `setGanttBound()` 이식 — 간트 x축 범위를 계획기간과 조회구간에서
// 정한다(gantt-implementation.md §5-2a D5: **축이 하루만 어긋나도 프로즌존·다운타임 위치가 전부
// 밀리므로 old 규칙을 그대로 옮긴다**).
//  - 계획기간 전체(isEntirePlanPeriod) → 버전 계획 시작~종료 그대로
//  - 아니면: 조회구간을 계획기간 안으로 클램프 → 종료일에 하루 더함(그 날 자체를 포함) →
//    계획기간 경계와 같은 날이 아니면 자정으로 정규화
//  - 조회구간이 계획기간과 아예 겹치지 않거나 값이 없으면 계획기간으로 폴백
export const buildGanttBound = ({
  planStartTs, planEndTs, startDate, endDate, isEntirePlanPeriod, dataStartTs, dataEndTs
}) => {
  const planStart = toMs(planStartTs);
  const planEnd = toMs(planEndTs);

  if (Number.isNaN(planStart) || Number.isNaN(planEnd) || planEnd <= planStart) return null;

  let startTs = planStart;
  let endTs = planEnd;
  let searchApplied = false;

  if (!isEntirePlanPeriod) {
    const searchStart = toMs(startDate);
    const searchEnd = toMs(endDate);

    if (!Number.isNaN(searchStart) && !Number.isNaN(searchEnd)) {
      const from = Math.max(planStart, searchStart);
      const inclusiveEnd = Math.min(planEnd, startOfDay(new Date(searchEnd)).getTime() + 24 * MS_PER_HOUR);

      if (inclusiveEnd > from) {
        // old: 조회 시작이 계획 시작과 같은 날이면 계획 시작 시각을 그대로, 아니면 그 날 자정부터.
        startTs = isSameDay(from, planStart) ? from : startOfDay(new Date(from)).getTime();
        endTs = inclusiveEnd;
        searchApplied = true;
      }
    }
  }

  // ⚠️ old `setGanttBound`는 여기서 끝이라 **계획기간 밖 작업이 축에 아예 안 들어갔다.**
  // 실데이터에는 계획 시작보다 이른 작업(WIP/선행 재고 투입 등)이 있어서, 그대로 두면
  // "작업 바는 안 보이는데 의존선만 남는" 상태가 된다(2026-08-18 확인). 조회된 데이터 구간까지
  // 축을 넓혀 **조회 결과는 항상 화면에 들어오게** 한다(old와의 의도적 차이).
  //
  // 단 **사용자가 조회 구간을 지정한 경우엔 넓히지 않는다**(2026-08-24, §7-14). 백엔드 필터가
  // 구간을 "포함"이 아니라 "걸침"으로 잡아서(주문 간트는 `woPlan.endTs BETWEEN`, 자원 간트는
  // 활동 기간 겹침) 구간보다 이른 작업이 거의 항상 섞여 들어온다. 그걸 합집합하면 축이 데이터
  // 시작으로 당겨져 **조회 시작일을 바꿔도 축이 안 변하는** 것처럼 보인다. 구간 지정은 사용자의
  // 명시적 의사라 데이터가 덮어쓰면 안 되고, 구간 밖으로 걸친 부분은 old처럼 잘려서 보인다.
  if (!searchApplied) {
    const dataStart = toMs(dataStartTs);
    const dataEnd = toMs(dataEndTs);
    if (!Number.isNaN(dataStart)) startTs = Math.min(startTs, dataStart);
    if (!Number.isNaN(dataEnd)) endTs = Math.max(endTs, dataEnd);
  }

  return endTs > startTs ? { startTs, endTs } : null;
};
